import { useState } from 'react'
import { ArrowDropDown } from '@mui/icons-material'
import { useSsoEnabled, useAuthHintQuery } from '@global/hooks/queries/useAuthHintQuery'
import { AuthScheme } from '@global/api/types'
import { useAuth0 } from '@auth0/auth0-react'
import { MenuItem, Divider } from '@mui/material'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import * as authActions from '@authed/auth.actions'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { Dropdown } from '@components/menu/dropdown'
import { UserChip } from '@components/user/userChip'
import { User, Role } from '@global/types'
import { useAppSelector } from '@redux/hooks'
import { t } from '@lingui/core/macro'
import { SystemStyleObject } from '@mui/system'

const ROUTES = {
  profile: '/admin/settings',
}

export type UserPartial = Pick<User, 'avatar_slug' | 'name' | 'role' | 'mock' | 'super_admin'>

interface UserMenuItemProps {
  children: React.ReactNode
  clickable?: boolean
  sx?: SystemStyleObject
  onClick?: React.MouseEventHandler<HTMLLIElement>
}

const UserMenuItem = ({ children, clickable = true, sx = {}, onClick }: UserMenuItemProps) => (
  <MenuItem dense disableRipple={!clickable} sx={{ typography: 'body2', ...sx }} onClick={onClick}>
    {children}
  </MenuItem>
)

interface UserMenuProps {
  user: UserPartial
}

export const UserMenu = ({ user }: UserMenuProps) => {
  const dispatch = useDispatch()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const { data: authHint } = useAuthHintQuery()
  const { logout: auth0Logout } = useAuth0()

  const [SSO_LOGOUT = false] = useAppSelector(selectFeatureIncludes([FeatureDict.SSO_LOGOUT]))
  const ssoEnabled = useSsoEnabled()

  const friendlyRole = user.role ? getRoleDisplayName(user) : null

  return (
    <Dropdown
      id="user-menu"
      icon={<UserChip user={user} label={<ArrowDropDown />} onClick={handleClick} size="small" />}
      anchorEl={menuAnchor}
      onClose={handleClose}
      onClick={handleClose}
      sx={{
        root: {
          cursor: 'default',
        },
      }}
    >
      <UserMenuItem
        clickable={false}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          cursor: 'default',
          '&:hover': {
            background: 'none',
          },
        }}
      >
        <strong>{user.name}</strong>
        {friendlyRole && <em style={{ fontSize: '12px' }}>{friendlyRole}</em>}
      </UserMenuItem>
      <Divider />
      <Link to={ROUTES.profile}>
        <UserMenuItem>{t`Profile`}</UserMenuItem>
      </Link>
      {(!ssoEnabled || SSO_LOGOUT) && (
        <UserMenuItem onClick={handleLogout}>{t`Logout`}</UserMenuItem>
      )}
    </Dropdown>
  )

  function handleClick(event: React.MouseEvent) {
    setMenuAnchor(event.currentTarget as HTMLElement)
  }

  function handleClose() {
    setMenuAnchor(null)
  }

  function handleLogout() {
    if (user.mock) return

    if (ssoEnabled && SSO_LOGOUT) {
      window.location.href = '/logout'
      return
    }

    if (authHint?.auth_scheme === AuthScheme.dsc_universal_login) {
      void auth0Logout({ logoutParams: { federated: true, returnTo: window.location.origin } })
      return
    }

    dispatch(authActions.logout())
  }

  function getRoleDisplayName(user: UserPartial): string {
    if (user.super_admin) return t`Super Admin`

    switch (user.role) {
      case Role.OWNER:
        return t`Admin`
      case Role.MANAGER:
        return t`Manager`
      default:
        return t`User`
    }
  }
}
